import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import qs from 'qs';
import compact from '@/utils/compact';
import { MainContents } from '@/components/MainContents';
import Loading from '@/components/Loading';
import Tabs, { type TabItem } from '@/components/Tabs';
import useSharedJobPosts from '@/features/agent/shared_job_posts/hooks/useSharedJobPosts';
import type { SearchQuery } from '@/features/agent/shared_job_posts/hooks/useSharedJobPosts';
import BackToSearchBar from '@/components/BackToSearchBar';
import type { AgentApiSharedJobPostsItem } from '@/types/serializers';
import SearchForm from '@/features/agent/shared_job_posts/components/SearchForm';
import SharedJobPostCard from '@/features/agent/shared_job_posts/components/SharedJobPostCard';
import SelectionBar from '@/features/agent/provided_job_posts/components/SelectionBar';
import useBulkSuggestSharedJobPosts from '@/features/agent/shared_job_posts/hooks/useBulkSuggestSharedJobPosts';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { MaterialIcon } from '@/components/MaterialIcon';
import ProviderConnectionDropdown from '@/features/agent/shared_job_posts/components/ProviderConnectionDropdown';
import SyncRequestButton from '@/features/agent/shared_job_posts/components/SyncRequestButton';
import pickSyncRequestSearchConditions from '@/features/agent/shared_job_posts/lib/pickSyncRequestSearchConditions';

// localStorageのキー名
const SEARCH_QUERY_STORAGE_KEY = 'sharedJobPostsSearchQuery';

// 旧来 localStorage / URL クエリに保存されている `created_at:*` を `id:*` に正規化する (Issue #2629)。
// OpenSearch mapping に created_at が無いため、サイレントに default sort にフォールバックして
// 「作成順」のつもりが関連度/ID 順になっていた。連番 id を代理にするよう揃える。
const normalizeSortValue = (sort?: string): string => {
  if (!sort) return 'updated_at:desc';
  if (sort === 'created_at:desc') return 'id:desc';
  if (sort === 'created_at:asc') return 'id:asc';
  return sort;
};

// タブの定義
const TAB_OPTIONS = [
  { label: '自社求人', value: 'own', path: '/agent/provided_job_posts' },
  { label: '求人データベース', value: 'shared', path: '/agent/shared_job_posts' },
] as const;

type TabValue = (typeof TAB_OPTIONS)[number]['value'];

// 検索フォームのスキーマ
const searchSchema = z.object({
  // 横断検索キーワード (Issue #2465)
  search_query: z.string().optional(),
  // 会社名フィルタ
  company_name: z.string().optional(),
  job_position_name: z.string().optional(),
  min_salary: z.string().optional(),
  max_salary: z.string().optional(),
  salary: z.string().optional(),
  // Issue #2530: 年収未記載を含めるフラグ（'1' で ON）
  salary_undisclosed: z.string().optional(),
  min_fee_rate: z.string().optional(),
  max_fee_rate: z.string().optional(),
  fee_rate: z.string().optional(),
  min_fee_value: z.string().optional(),
  max_fee_value: z.string().optional(),
  fee_value: z.string().optional(),
  sort: z.string(),
  provider: z.string().optional(),
  shared_job_post_list_id: z.string().optional(),
  // 都道府県コード絞り込み（Issue #3394）
  prefecture_codes: z.array(z.number()).optional(),
  // 雇用形態絞り込み（Issue #3216）
  employment_types: z.array(z.string()).optional(),
  employment_type_undisclosed: z.string().optional(),
  // 候補者年齢が応募可能な求人の絞り込み（Issue #3216）
  age: z.string().optional(),
  age_undisclosed: z.string().optional(),
  // 候補者の最終学歴が応募可能な求人の絞り込み（Issue #3216）
  education_level: z.string().optional(),
  education_level_undisclosed: z.string().optional(),
  job_type_inexperienced_ok: z.string().optional(),
  industry_inexperienced_ok: z.string().optional(),
});

export type FormValue = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/agent/shared_job_posts/')({
  component: SharedJobPosts,
  staticData: { title: '求人管理' },
});

function SharedJobPosts() {
  const searchRef = useRef<HTMLDivElement>(null);

  // 現在のパスからタブを判定
  const getCurrentTab = (): TabValue => {
    if (typeof window === 'undefined') return 'shared';
    const pathname = window.location.pathname;
    if (pathname.startsWith('/agent/shared_job_posts')) return 'shared';
    return 'own';
  };

  const currentTab = getCurrentTab();

  const form = useForm<FormValue>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      search_query: '',
      company_name: '',
      job_position_name: '',
      min_salary: '',
      max_salary: '',
      salary: '',
      salary_undisclosed: '',
      min_fee_rate: '',
      max_fee_rate: '',
      fee_rate: '',
      min_fee_value: '',
      max_fee_value: '',
      fee_value: '',
      sort: 'updated_at:desc',
      provider: undefined,
      shared_job_post_list_id: undefined,
      prefecture_codes: [],
      employment_types: [],
      // 雇用形態・年齢・最終学歴は多くのプロバイダでデータ未記載のため、既定で「未記載を含む」ON
      employment_type_undisclosed: '1',
      age: '',
      age_undisclosed: '1',
      education_level: '',
      education_level_undisclosed: '1',
      job_type_inexperienced_ok: '',
      industry_inexperienced_ok: '',
    },
  });

  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    search_query: '',
    company_name: '',
    job_position_name: '',
    min_salary: '',
    max_salary: '',
    salary: '',
    salary_undisclosed: '',
    min_fee_rate: '',
    max_fee_rate: '',
    fee_rate: '',
    min_fee_value: '',
    max_fee_value: '',
    fee_value: '',
    sort: 'updated_at:desc',
    provider: undefined,
    shared_job_post_list_id: undefined,
    prefecture_codes: [],
    employment_types: [],
    employment_type_undisclosed: '1',
    age: '',
    age_undisclosed: '1',
    education_level: '',
    education_level_undisclosed: '1',
    job_type_inexperienced_ok: '',
    industry_inexperienced_ok: '',
  });

  // localStorageに検索条件を保存する関数
  const saveSearchQueryToLocalStorage = (sq: SearchQuery) => {
    try {
      const serialized = JSON.stringify(sq);
      localStorage.setItem(SEARCH_QUERY_STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Failed to save search query to localStorage:', error);
    }
  };

  // localStorageから検索条件を読み込む関数
  const loadSearchQueryFromLocalStorage = (): SearchQuery | null => {
    try {
      const stored = localStorage.getItem(SEARCH_QUERY_STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored) as SearchQuery;
    } catch (error) {
      console.error('Failed to load search query from localStorage:', error);
      return null;
    }
  };

  const _onSubmit = (data: FormValue) => {
    const sq: SearchQuery = {
      search_query: data.search_query || undefined,
      company_name: data.company_name || undefined,
      job_position_name: data.job_position_name || undefined,
      min_salary: data.min_salary || undefined,
      max_salary: data.max_salary || undefined,
      salary: data.salary || undefined,
      salary_undisclosed: data.salary_undisclosed || undefined,
      min_fee_rate: data.min_fee_rate || undefined,
      max_fee_rate: data.max_fee_rate || undefined,
      fee_rate: data.fee_rate || undefined,
      min_fee_value: data.min_fee_value || undefined,
      max_fee_value: data.max_fee_value || undefined,
      fee_value: data.fee_value || undefined,
      sort: data.sort,
      provider: data.provider || undefined,
      shared_job_post_list_id: data.shared_job_post_list_id || undefined,
      prefecture_codes:
        data.prefecture_codes && data.prefecture_codes.length > 0
          ? data.prefecture_codes
          : undefined,
      employment_types:
        data.employment_types && data.employment_types.length > 0
          ? data.employment_types
          : undefined,
      // OFF を URL / localStorage に残すため '0' を明示送信する（BE の truthy? は '0' を false 扱い）
      employment_type_undisclosed: data.employment_type_undisclosed === '1' ? '1' : '0',
      age: data.age || undefined,
      age_undisclosed: data.age_undisclosed === '1' ? '1' : '0',
      education_level: data.education_level || undefined,
      education_level_undisclosed: data.education_level_undisclosed === '1' ? '1' : '0',
      job_type_inexperienced_ok: data.job_type_inexperienced_ok === '1' ? '1' : undefined,
      industry_inexperienced_ok: data.industry_inexperienced_ok === '1' ? '1' : undefined,
    };

    setSearchQuery(sq);
    saveSearchQueryToLocalStorage(sq);
    const queryString = qs.stringify(compact(sq), { arrayFormat: 'brackets' });
    history.pushState(null, '', `?${queryString}`);
  };

  const setSearchQueryFromQueryString = () => {
    const q = qs.parse(location.search, { ignoreQueryPrefix: true });

    const hasQueryParams = Object.keys(q).length > 0;

    let sq: SearchQuery;

    if (hasQueryParams) {
      sq = {
        search_query: (q.search_query ?? '') as string,
        company_name: (q.company_name ?? '') as string,
        job_position_name: (q.job_position_name ?? '') as string,
        min_salary: (q.min_salary ?? '') as string,
        max_salary: (q.max_salary ?? '') as string,
        salary: (q.salary ?? '') as string,
        salary_undisclosed: (q.salary_undisclosed ?? '') as string,
        min_fee_rate: (q.min_fee_rate ?? '') as string,
        max_fee_rate: (q.max_fee_rate ?? '') as string,
        fee_rate: (q.fee_rate ?? '') as string,
        min_fee_value: (q.min_fee_value ?? '') as string,
        max_fee_value: (q.max_fee_value ?? '') as string,
        fee_value: (q.fee_value ?? '') as string,
        sort: normalizeSortValue(q.sort as string | undefined),
        provider: q.provider ? (q.provider as string) : undefined,
        shared_job_post_list_id: q.shared_job_post_list_id
          ? (q.shared_job_post_list_id as string)
          : undefined,
        prefecture_codes: Array.isArray(q.prefecture_codes)
          ? (q.prefecture_codes as string[])
              .map((code) => Number(code))
              .filter((code) => !Number.isNaN(code))
          : [],
        employment_types: Array.isArray(q.employment_types) ? (q.employment_types as string[]) : [],
        employment_type_undisclosed: (q.employment_type_undisclosed ?? '1') as string,
        age: (q.age ?? '') as string,
        age_undisclosed: (q.age_undisclosed ?? '1') as string,
        education_level: (q.education_level ?? '') as string,
        education_level_undisclosed: (q.education_level_undisclosed ?? '1') as string,
        job_type_inexperienced_ok: (q.job_type_inexperienced_ok ?? '') as string,
        industry_inexperienced_ok: (q.industry_inexperienced_ok ?? '') as string,
      };
      saveSearchQueryToLocalStorage(sq);
    } else {
      const storedQuery = loadSearchQueryFromLocalStorage();
      if (storedQuery) {
        sq = { ...storedQuery, sort: normalizeSortValue(storedQuery.sort) };
        const queryString = qs.stringify(compact(sq), { arrayFormat: 'brackets' });
        history.replaceState(null, '', `?${queryString}`);
      } else {
        sq = {
          search_query: '',
          company_name: '',
          job_position_name: '',
          min_salary: '',
          max_salary: '',
          salary: '',
          salary_undisclosed: '',
          min_fee_rate: '',
          max_fee_rate: '',
          fee_rate: '',
          min_fee_value: '',
          max_fee_value: '',
          fee_value: '',
          sort: 'updated_at:desc',
          provider: undefined,
          shared_job_post_list_id: undefined,
          prefecture_codes: [],
          employment_types: [],
          employment_type_undisclosed: '1',
          age: '',
          age_undisclosed: '1',
          education_level: '',
          education_level_undisclosed: '1',
          job_type_inexperienced_ok: '',
          industry_inexperienced_ok: '',
        };
      }
    }
    setSearchQuery(sq);

    form.setValue('search_query', sq.search_query ?? '');
    form.setValue('company_name', sq.company_name ?? '');
    form.setValue('job_position_name', sq.job_position_name ?? '');
    form.setValue('min_salary', sq.min_salary ?? '');
    form.setValue('max_salary', sq.max_salary ?? '');
    form.setValue('salary', sq.salary ?? '');
    form.setValue('salary_undisclosed', sq.salary_undisclosed ?? '');
    form.setValue('min_fee_rate', sq.min_fee_rate ?? '');
    form.setValue('max_fee_rate', sq.max_fee_rate ?? '');
    form.setValue('fee_rate', sq.fee_rate ?? '');
    form.setValue('min_fee_value', sq.min_fee_value ?? '');
    form.setValue('max_fee_value', sq.max_fee_value ?? '');
    form.setValue('fee_value', sq.fee_value ?? '');
    form.setValue('sort', normalizeSortValue(sq.sort));
    form.setValue('provider', sq.provider);
    form.setValue('shared_job_post_list_id', sq.shared_job_post_list_id);
    form.setValue('prefecture_codes', sq.prefecture_codes ?? []);
    form.setValue('employment_types', sq.employment_types ?? []);
    form.setValue('employment_type_undisclosed', sq.employment_type_undisclosed ?? '1');
    form.setValue('age', sq.age ?? '');
    form.setValue('age_undisclosed', sq.age_undisclosed ?? '1');
    form.setValue('education_level', sq.education_level ?? '');
    form.setValue('education_level_undisclosed', sq.education_level_undisclosed ?? '1');
    form.setValue('job_type_inexperienced_ok', sq.job_type_inexperienced_ok ?? '');
    form.setValue('industry_inexperienced_ok', sq.industry_inexperienced_ok ?? '');
  };

  useEffect(() => {
    setSearchQueryFromQueryString();
    window.addEventListener('popstate', setSearchQueryFromQueryString);
    return () => window.removeEventListener('popstate', setSearchQueryFromQueryString);
  }, []);

  // タブアイテムの生成
  const tabItems: TabItem[] = useMemo(() => {
    return TAB_OPTIONS.map((option) => {
      return {
        label: option.label,
        current: currentTab === option.value,
        to: option.path,
      };
    });
  }, [currentTab]);

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useSharedJobPosts(searchQuery);

  const totalCount = data?.pages?.[0]?.total_count ?? 0;

  const syncRequestSearchConditions = useMemo(
    () => pickSyncRequestSearchConditions(searchQuery),
    [searchQuery]
  );

  // 検索条件が変わったら選択状態をリセットする
  // (前の検索結果での選択が残ると selectedIds.size >= totalCount による「全選択済み」判定が誤るため)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery]);

  const dataRef = useRef(data);
  dataRef.current = data;

  const handleSelectAll = useCallback(async () => {
    setIsSelectingAll(true);
    try {
      const MAX_PAGES = 200;
      let fetchCount = 0;
      let latestData = dataRef.current;
      let nextHasNextPage = hasNextPage;

      // fetchNextPage の戻り値の data を使う（dataRef は再レンダー前で古いままになり得る）
      while (nextHasNextPage && fetchCount < MAX_PAGES) {
        const result = await fetchNextPage();
        if (result.isError) break;
        latestData = result.data ?? latestData;
        nextHasNextPage = Boolean(result.hasNextPage);
        fetchCount++;
      }

      const allIds = new Set<number>();
      if (latestData?.pages) {
        for (const page of latestData.pages) {
          for (const record of page.records) {
            allIds.add(record.id);
          }
        }
      }
      setSelectedIds(allIds);
    } finally {
      setIsSelectingAll(false);
    }
  }, [hasNextPage, fetchNextPage]);

  const sharedJobPosts = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: { records: AgentApiSharedJobPostsItem[] }) => page.records);
  }, [data?.pages]);

  const { handleWebShare, handlePdfShare, handleSuggestToCandidates, dialogs } =
    useBulkSuggestSharedJobPosts({
      sharedJobPosts,
      selectedIds,
    });

  const handleCheckedChange = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleScroll = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll]);

  return (
    <FormProvider {...form}>
      <MainContents>
        {/* タブ */}
        <div className="mb-md w-[520px]">
          <Tabs items={tabItems} />
        </div>
        {/* ヘッダー部分 */}
        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold">{isLoading ? '---' : totalCount}</span>
            <span className="text-sm">件</span>
          </div>
          <ProviderConnectionDropdown />
        </div>
        <SyncRequestButton searchConditions={syncRequestSearchConditions} />
        <div ref={searchRef} className="mt-sm">
          <SearchForm
            onSubmit={_onSubmit}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            isSelectingAll={isSelectingAll}
            isAllSelected={totalCount > 0 && selectedIds.size >= totalCount}
            totalCount={totalCount}
            hasResults={sharedJobPosts.length > 0}
          />
        </div>
        <BackToSearchBar targetRef={searchRef} />
        <div className="mt-md space-y-6 pb-8">
          {isLoading ? (
            <div className="flex justify-center py-md">
              <Loading size="small" />
            </div>
          ) : (
            <>
              {sharedJobPosts.length > 0 ? (
                <div className="space-y-4">
                  {sharedJobPosts.map((post: AgentApiSharedJobPostsItem) => (
                    <SharedJobPostCard
                      key={post.id}
                      post={post}
                      isChecked={selectedIds.has(post.id)}
                      onCheckedChange={(checked) => handleCheckedChange(post.id, checked)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex justify-center bg-[#F6F6F6] py-6">
                  <div className="flex w-full max-w-[1142px] flex-col items-center justify-center gap-6">
                    {/* EmptyStatus */}
                    <div className="flex flex-col items-center gap-6">
                      <div className="flex flex-col items-center gap-2">
                        <p className="text-base leading-normal font-bold text-[#262626]">
                          求人データがありません
                        </p>
                        <div className="opacity-50">
                          <img
                            src="/agent/job_change_processes/no-data.svg"
                            alt="No data"
                            className="h-[144px] w-[148px]"
                          />
                        </div>
                      </div>
                      {/* アカウントを追加するボタン */}
                      <Link to="/agent/linked_accounts">
                        <Button color="tertiary" size="md">
                          アカウントを追加する
                          <MaterialIcon name="keyboard_arrow_right" className="size-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
              {(isLoading || isFetchingNextPage) && sharedJobPosts.length > 0 && (
                <div className="flex justify-center py-md">
                  <Loading size="small" />
                </div>
              )}
            </>
          )}
        </div>
      </MainContents>
      <SelectionBar
        selectedCount={selectedIds.size}
        onDeselectAll={handleDeselectAll}
        onWebShare={handleWebShare}
        onPdfShare={handlePdfShare}
        onSuggestToCandidates={handleSuggestToCandidates}
      />
      {dialogs}
    </FormProvider>
  );
}
